/**
 * 판매 현황 시안의 화면 말 · 셈 — 목록 · 상세가 같이 쓴다(10-28 회의 시안, 회의 2026-10-21 §9).
 * 진행상태는 이카운트 글 그대로 온다 — 여기서 갈래(진행 중 · 납품 완료 · 임대 · 자산)로 읽는다.
 */
import type { SalesPreviewItem, SalesPreviewView } from '../../api/client';
import { fmtNumber } from '../../lib/cells';

/** 판매 한 건의 갈래 — 상태 칩 색 · 상세 머리 띠 색 */
export type SalesKind = 'open' | 'done' | 'rental' | 'asset' | 'other';

/** 목록 탭 — 이카운트 진행상태 글 하나에 하나('' = 전부) */
export const SALES_STATUS_TABS: readonly { label: string; status: string }[] = [
  { label: '전부', status: '' },
  { label: '진행 중', status: '확인' },
  { label: '납품 완료', status: '배송완료' },
  { label: '임대', status: '임대(반납시 삭제)' },
  { label: '자산', status: '자산' },
];

/** 단계 하나를 넘기는 쪽 — 영업 · 제품팀, 납품 완료는 앞 단계가 다 차면 저절로 */
export type SalesStepActor = 'sales' | 'product' | 'auto';

export const SALES_STEP_ACTOR_WORD: Record<SalesStepActor, string> = { sales: '영업', product: '제품팀', auto: '자동' };

/** 단계 하나 — 이름 · 넘기는 쪽 */
export type SalesStep = { label: string; actor: SalesStepActor };

/** 회의 §9 의 진행 단계 여섯 — 일직선(현민 2026-10-27 "일직선이 아니면 직관적이지 않다"). 상세의 단계 줄 */
export const SALES_STEPS: readonly SalesStep[] = [
  { label: '사전 등록', actor: 'sales' },
  { label: '출고 요청', actor: 'sales' },
  { label: '세팅 완료', actor: 'product' },
  { label: '출고 완료', actor: 'product' },
  { label: '영업 기입', actor: 'sales' },     // 세팅 정보 · 출고 정보
  { label: '납품 완료', actor: 'auto' },
];

/** 한 건에 쌓이는 기록의 종류(회의 §9 01:28) — 제품팀의 세팅 내역 · 요청사항 · 이슈, 출고 뒤 기사님 번호 · 송장 */
export type SalesNoteKind = 'setup' | 'request' | 'issue' | 'shipping';

export const SALES_NOTE_KINDS: readonly SalesNoteKind[] = ['setup', 'request', 'issue', 'shipping'];

export const SALES_NOTE_KIND_WORD: Record<SalesNoteKind, string> = {
  setup: '세팅 내역',
  request: '요청사항',
  issue: '이슈',
  shipping: '출고 정보',
};

/** 기록 한 줄 — 누가 · 언제 · 종류 · 글 */
export type SalesNote = { kind: SalesNoteKind; text: string; who: string; at: string };

/** 이카운트 진행상태 → 갈래. 모르는 글은 other */
export function salesKindOf(status: string | null): SalesKind {
  if (status === null) return 'other';
  if (status.includes('임대')) return 'rental';
  if (status.includes('자산')) return 'asset';
  if (status === '배송완료') return 'done';
  if (status === '확인') return 'open';
  return 'other';
}

/** 갈래 → 화면 말(칩 글). 이카운트 글은 칩 title 로 */
export const SALES_KIND_WORD: Record<SalesKind, string> = {
  open: '진행 중',
  done: '납품 완료',
  rental: '임대',
  asset: '자산',
  other: '기타',
};

/** 이카운트 상태로 짐작한 지금 단계 번호(0부터) — 시안. 확인 = 사전 등록, 임대 · 자산 = 출고 완료, 배송완료 = 납품 완료 */
export function stepIndexOf(kind: SalesKind): number {
  switch (kind) {
    case 'open': return 0;
    case 'rental': return 3;
    case 'asset': return 3;
    case 'done': return SALES_STEPS.length - 1;
    case 'other': return 0;
    default: throw new Error(`unhandled switch case: ${String(kind satisfies never)}`);
  }
}

/** 금액 글 — null 이면 "—" */
export function wonText(amount: number | null): string {
  return amount === null ? '—' : `${fmtNumber(Math.round(amount))}`;
}

/** 마진율 — 원가를 찾은 줄의 공급가액(= 원가 + 마진) 대비. 원가가 없으면 null */
export function marginRateOf(view: Pick<SalesPreviewView, 'cost_amount' | 'margin_amount'>): number | null {
  if (view.cost_amount === null || view.margin_amount === null) return null;
  const costedSupply = view.cost_amount + view.margin_amount;
  return costedSupply === 0 ? null : (view.margin_amount / costedSupply) * 100;
}

/** 품목명 — 목록 · 상세가 같이. 이카운트 품목명[규격]은 "파트넘버 [설명]" 꼴이라 앞 파트넘버를 떼고 대괄호 안만. 그 꼴이 아니면 글 그대로 */
export function itemNameOf(item: Pick<SalesPreviewItem, 'part_number' | 'spec'>): string {
  if (item.spec === null) return '';
  const bracket = /^\s*(\S+)\s*\[(.*)\]\s*$/.exec(item.spec);
  if (bracket === null || item.part_number === null || bracket[1] !== item.part_number) return item.spec;
  return bracket[2] ?? item.spec;
}

/** 판매 한 건 상세의 주소 — 일자-No.에 `/` 가 들어 질의로 */
export function salesDetailHrefOf(slipNo: string): string {
  return `/preview/sales/detail?slip=${encodeURIComponent(slipNo)}`;
}

/** 품목 → 제품 목록에서 그 파트넘버로 찾기(시안 — 이카운트 줄은 제품 id 가 없다) */
export function productSearchHrefOf(partNumber: string): string {
  return `/products?q=${encodeURIComponent(partNumber)}`;
}
